import { useCallback } from 'react'
import {
  IAs2TestResultStatuses,
  ITestAs2ConnectionConfigOutboundInput,
  ITestFtpConnectionConfigInput,
} from 'types/graphqlTypes'
import { useGlobalToast } from 'transfix-ui'
import { useTestAs2ConnectionConfigOutboundMutation } from '../graphql/TestAs2ConnectionConfigOutbound'
import { useTestFtpConnectionConfigMutation } from '../graphql/TestFtpConnectionConfig'

export const useTestConnectionConfig = () => {
  const [testAs2ConnectionConfigOutboundMutation] = useTestAs2ConnectionConfigOutboundMutation()

  const [testFtpConnectionConfigMutation] = useTestFtpConnectionConfigMutation()

  const { openToast } = useGlobalToast()

  const testAs2ConnectionConfig = useCallback(
    async (input: ITestAs2ConnectionConfigOutboundInput) => {
      try {
        const response = await testAs2ConnectionConfigOutboundMutation({
          variables: {
            input,
          },
        })

        const { data } = response || {}
        const { testAs2ConnectionConfigOutbound } = data || {}
        const { errors = [], as2ConnectionConfigOutboundTestReport } =
          testAs2ConnectionConfigOutbound || {}

        const filteredErrors = errors.filter(item => !!item.message)

        if (filteredErrors.length > 0) {
          const [firstError] = filteredErrors || []
          const { message } = firstError || {}
          openToast({
            toastMessage: message,
            toastType: 'alert',
          })
          return
        }

        const { errorMessage, signatureVerificationError, status } =
          as2ConnectionConfigOutboundTestReport || {}

        if (status === IAs2TestResultStatuses.ERROR) {
          openToast({
            toastMessage: errorMessage || signatureVerificationError || 'Unknown error occured',
            toastType: 'alert',
          })
        } else if (status === IAs2TestResultStatuses.SUCCESS_WITH_WARNING) {
          openToast({
            toastMessage: 'Connection successful but with warnings',
            toastType: 'default',
          })
        } else {
          openToast({
            toastMessage: 'Connection successful',
            toastType: 'default',
          })
        }
      } catch (e) {
        openToast({
          toastMessage: e.message,
          toastType: 'alert',
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testAs2ConnectionConfigOutboundMutation]
  )

  const testFtpConnectionConfig = useCallback(
    async (input: ITestFtpConnectionConfigInput) => {
      try {
        const response = await testFtpConnectionConfigMutation({
          variables: {
            input,
          },
        })

        const { data } = response || {}
        const { testFtpConnectionConfig } = data || {}
        const { errors = [], ftpConnectionConfigTestReport } = testFtpConnectionConfig || {}

        const filteredErrors = errors.filter(item => !!item.message)

        if (filteredErrors.length > 0) {
          const [firstError] = filteredErrors || []
          const { message } = firstError || {}
          openToast({
            toastMessage: message,
            toastType: 'alert',
          })
          return
        }

        const { errorMessage, success } = ftpConnectionConfigTestReport || {}

        if (!success) {
          openToast({
            toastMessage: errorMessage || 'Unknown error occured',
            toastType: 'alert',
          })
        } else {
          openToast({
            toastMessage: 'Connection successful',
            toastType: 'default',
          })
        }
      } catch (e) {
        openToast({
          toastMessage: e.message,
          toastType: 'alert',
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testAs2ConnectionConfigOutboundMutation]
  )

  return {
    testAs2ConnectionConfig,
    testFtpConnectionConfig,
  }
}
